import { useEffect, useState, useMemo } from "react";
import api, { formatMoney } from "@/lib/api";
import { PageHeader, StatCard, Panel, Pill } from "@/components/Bits";
import { useAuth } from "@/contexts/AuthContext";
import { Baby, ChalkboardTeacher, CurrencyCircleDollar, MagnifyingGlass, Bell, GraduationCap, Calendar } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { Link } from "react-router-dom";

export default function BranchDashboard() {
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [students, setStudents] = useState([]);
  const [events, setEvents] = useState([]);
  const [trend, setTrend] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const params = user?.branch_id ? `?branch_id=${user.branch_id}` : "";
        const [o, e, s, ev, t] = await Promise.all([
          api.get(`/analytics/overview${params}`),
          api.get(`/enquiries${params}`),
          api.get(`/students${params}`),
          api.get(`/events${params}`),
          api.get(`/analytics/attendance-trend?days=7${user?.branch_id ? `&branch_id=${user.branch_id}` : ""}`),
        ]);
        setOverview(o.data); setEnquiries(e.data.slice(0,8)); setStudents(s.data); setEvents(ev.data.slice(0,4));
        setTrend(t.data);
      } catch (err) { console.error(err); }
    })();
  }, [user]);

  const recentEnq = useMemo(() => enquiries.filter(e => e.status !== "converted").slice(0, 6), [enquiries]);

  return (
    <div>
      <PageHeader
        eyebrow="Branch admin"
        title="Your centre today"
        subtitle="Operations, admissions, attendance and money — calm and connected."
      />

      {overview && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard label="Students" value={overview.students_active} icon={Baby} accent="terracotta" />
          <StatCard label="Attendance today" value={`${overview.attendance_today_pct}%`} icon={ChalkboardTeacher} accent="sage" hint={`${overview.attendance_today_present} present`} />
          <StatCard label="Open enquiries" value={overview.enquiries_total - overview.admissions_converted} icon={MagnifyingGlass} accent="mustard" />
          <StatCard label="Pending fees" value={formatMoney(overview.fees_due)} icon={CurrencyCircleDollar} accent="lavender" />
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        <Panel title="Attendance — last 7 days" className="lg:col-span-2">
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EBE5DF" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#6E6761" }} />
                <YAxis tick={{ fontSize: 11, fill: "#6E6761" }} />
                <Tooltip />
                <Bar dataKey="pct" fill="#E56B55" radius={[8, 8, 0, 0]} name="Attendance %" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title="Upcoming events">
          <div className="space-y-3">
            {events.map((ev) => (
              <div key={ev.event_id} className="p-3 rounded-2xl bg-paper-alt">
                <div className="font-semibold text-sm">{ev.name}</div>
                <div className="text-xs text-ink-soft mt-0.5">{ev.event_date}</div>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Panel title="Live enquiries" action={<Link to="/app/enquiries" className="text-sm text-terracotta font-semibold">View all →</Link>}>
          <div className="space-y-3">
            {recentEnq.map((e) => (
              <div key={e.enquiry_id} className="flex items-center justify-between p-3 rounded-2xl border border-border">
                <div>
                  <div className="font-semibold text-sm">{e.parent_name} <span className="text-ink-soft font-normal">· {e.child_name}</span></div>
                  <div className="text-xs text-ink-soft">{e.phone} · {e.source}</div>
                </div>
                <Pill tone={e.status === "demo_scheduled" ? "mustard" : e.status === "visited" ? "sage" : "lavender"}>{e.status.replace("_", " ")}</Pill>
              </div>
            ))}
          </div>
        </Panel>

        <Panel title="Roster preview" action={<Link to="/app/students" className="text-sm text-terracotta font-semibold">View all →</Link>}>
          <div className="space-y-2">
            {students.slice(0, 8).map((s) => (
              <div key={s.student_id} className="flex items-center justify-between p-3 rounded-2xl hover:bg-paper-alt">
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-full bg-terracotta-soft text-terracotta flex items-center justify-center font-semibold">{s.name[0]}</span>
                  <div>
                    <div className="font-semibold text-sm">{s.name}</div>
                    <div className="text-xs text-ink-soft">{s.age_group} · {s.class_name}</div>
                  </div>
                </div>
                <Pill tone="sage">{s.status}</Pill>
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}
